import globalScope from './modules/global-scope.mjs';
import * as runtimeSupport from './modules/runtime-support.mjs';
import * as util from './modules/util.mjs';
import * as vars from './modules/posts/vars.mjs';
import * as displaySupport from './modules/posts/display-support.mjs';
import * as clickSupport from './modules/posts/click-support.mjs';
import * as postsLoaderSupport from './modules/posts/loader-support.mjs';
import * as siteSupport from './modules/site-support.mjs';

import * as siteAccessLogging from 'site-access-logging';

// prismjs/themes/prism.css is managed separately, not via import here.


/**
 * Divide up the scripting for the post pages of the app among several bundles so as to optimize 
 * the delivery of functionality to the broswer client based on criticality. Part #1 provides 
 * support for mutating the display and handling clicks.
 */


/*
 * Define custom namespace to prevent pollution of the global scope.
 */
globalScope.mhsBlogNs = {};

/*
 * Populate custom namespace using an IIFE (Immediately Invoked Function Expression). More info: 
 *	https://stackoverflow.com/a/5947280/176247
 */
(function( mhsBlogNs ) {

/**
 * Bootstrap the app for post pages. Serves as the entry (initial load) point for all scripting.
 */
mhsBlogNs.bootstrapAppForPosts = function() {
	/*
	 * Disable interactivity for unsupported browsers. This means no comment submissions,  
	 * lightbox effects on images, etc.
	 */
	if (runtimeSupport.browserUnsupported) {
		displaySupport.showBrowserWarning();

		displaySupport.showLeadImage();
		
		displaySupport.stackAfterPostPanes();
	} else {
		if (!runtimeSupport.browserFullFeatured) {
			runtimeSupport.polyfillAcceptableBrowser();
		}

		displaySupport.displayPostSubscribeConfirmMessage();

		displaySupport.showLeadImage();

		const commentCntLinkEle = document.querySelector('.post-comment-count');
		// If the post has no comments, no comment count is displayed.
		if (commentCntLinkEle) {
			commentCntLinkEle.addEventListener('click', clickSupport.handleClickCommentCount);
		}

		clickSupport.prepareForAfterPostPaneInteraction();
		displaySupport.displayLinkedToComment();

		if (vars.commentReplyLinks) {
			// Add a click listener to all comment "reply" widgets on the page.
			vars.commentReplyLinks.forEach(function(commentReplyLinkEle) {
				commentReplyLinkEle.addEventListener('click', clickSupport.handleClickCommentReply);
			});
		}

		// Add a click listener to the "cancel reply" link re-used across the page.
		vars.commentReplyCancelLinkEle.addEventListener('click', clickSupport.handleClickCommentReplyCancel);
		/*
		 * Do not explicitly handle touchstart events in addition to click events, as doing so is 
		 * unnecessary, prone to bugs, and overly-complex. In order to prevent double-processing, 
		 * it is recommended that preventDefault() be called on the source event. However, doing 
		 * so globally prevents default browser processing of form constraints and submissions. 
		 *
		 * This leaves open the need for judicious handling of both click and touchstart events in 
		 * situations where default browser processing should always be prevented. However, 
		 * depending on the event-handling logic, the browser may interrupt calls to 
		 * preventDefault() with the following:
		 *  "[Intervention] Ignored attempt to cancel a touchstart event with cancelable=false, 
		 *	for example, because scrolling is in progress and cannot be interrupted."
		 * This error can be headed-off by checking for event.cancelable, but skipping 
		 * preventDefault()  when the event is not cancelable does nothing to prevent 
		 * double-processing.
		 *
		 * In the case of handling clicks on "cancel reply" links, double processing results in the 
		 * following:
		 * 1. The touchstart event is processed, causing the comment form to be moved away and the 
		 *	"cancel reply" link to be replaced with a "reply" link.
		 * 2. The click event is processed (with the now-restored "reply" link as the target), 
		 *	causing the comment form to be moved back to below the comment reply and the "reply" 
		 *	link to be replaced with a "cancel reply" link.
		 *
		 * My belief/hope is that simple click event-handling does not need to be supplemented with 
		 * touchstart event-handling.
		 */

		displaySupport.showCommentReplyLinks();
		displaySupport.showCommentForm();
	}

	/*
	 * Handle the simplified, email-address-only blog-subscribe form that is placed on each post.
	 */
	const subscribeFormButtonEle = document.getElementById('subscribe-form-button');
	if (subscribeFormButtonEle) {
		subscribeFormButtonEle.addEventListener('click', clickSupport.handleClickBlogSubscribe);
	}

	siteSupport.updateCopyrightEndDate();
	postsLoaderSupport.loadDownstreamScripts();

    siteAccessLogging.init({
    	//'stealthModeHostnamesArr': [],
    	'logTriggerUrl': '{{ global.logTriggerUrl }}',
      	'cloudflareToken': '{{ global.cloudflareToken }}', 
      	'forceCors': true
    });

	//siteAccessLogging.debug();

	siteAccessLogging.insertLogTrigger();
	siteAccessLogging.insertWebAnalytics();
	siteAccessLogging.displayStealthMode();
};

}(globalScope.mhsBlogNs));

/*
 * Activate as-needed to reveal elements that trigger Largest Contentful Paint events. More info: 
 *  https://web.dev/lcp/
 */
// new PerformanceObserver((entryList) => {
//   for (const entry of entryList.getEntries()) {
//     console.log('LCP candidate:', entry.startTime, entry);
//   }
// }).observe({type: 'largest-contentful-paint', buffered: true});

